<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { useTemplating, buildTemplateContext } from "#bb/composables/useTemplating";

const logger = useLogger("Markdown");

// 1. Expect 'id' from the parent.
const props = defineProps<{ id: string }>();

// 2. Access the definition data (read-only) from the content manager.
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

// 4. Try to inject report provider context and build template context
const report = inject<any | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));

// 5. Setup templating composable
const { applyTemplate } = useTemplating(templateContext, { logger });

// 6. Computed templated content
const templatedContent = computed(() => {
  if (report && !templateContext.value) {
    return "";
  }
  return applyTemplate(definitionData.value?.body || "");
});

// 7. Parse the markdown ourselves, keyed by the block id.
//    `<MDC>` defaults its async-data cache key to a hash of the *content*, so two
//    blocks holding identical text (e.g. two freshly-added blocks sharing the default
//    body) collide on a single shared cache entry — editing one then updates both.
//    Keying on the stable, unique block id isolates every block (fixes the bug where
//    editing one markdown block visually changed the others).
//
//    We block during SSR (non-lazy) so the candidate site still ships fully-rendered
//    markdown in its HTML, but stay lazy on the client so the block frame and its
//    sibling blocks paint immediately and the parsed body fills in without a Suspense
//    stall (fixes the bug where markdown appeared after every other block). Edits
//    refresh in place via `watch`, keeping the previous render visible (no flicker).
const markdownData = useAsyncData(
  `md-${props.id}`,
  async () => {
    const { parseMarkdown } = await import("@nuxtjs/mdc/runtime");
    return await parseMarkdown(templatedContent.value, {
      toc: false,
      contentHeading: false,
    });
  },
  { lazy: import.meta.client, watch: [templatedContent] },
);
if (import.meta.server) {
  await markdownData;
}
const { data: parsed } = markdownData;

// 4. Define a context menu "Edit" command.
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Markdown block editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this markdown block",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Markdown",
    icon: "lucide:letter-text",
  },
});
</script>

<template>
  <div
    :class="
      cn('prose max-w-none dark:prose-invert prose-headings:mt-0 prose-a:no-underline', $attrs.class ?? '')
    "
  >
    <!-- Render the templated markdown content (parsed per-block; see <script>). -->
    <MDCRenderer v-if="parsed?.body" :body="parsed.body" :data="parsed.data" tag="p" />
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.MARKDOWN,
  icon: "lucide:letter-text",
  editor: () => import("#bb/components/editor/content/markdown.vue"),
  type: "Markdown",
  description: "A markdown block for rich text content using Markdown syntax. Supports dynamic data templating via {{{ json.path }}} and simplified answer DSL {{ path/to/questionId }}.",
  default: {
    body: "# Markdown\n\nWrite your own markdown here",
  },
  categories: ["Content"],
  order: 13031,
};
</script>
